import { useCallback, useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';
import * as Sentry from '@sentry/browser';

function trimIfString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function isNonEmptyTrimmedString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function validateUpdateBeerInput(beerData) {
  if (!beerData || typeof beerData !== 'object') {
    return { error: 'Invalid beer data' };
  }

  const { beerId, breweryId, name, style, abv, description, styleName, styleType } = beerData;

  const requiredFieldChecks = [
    [beerId, 'Beer ID is required'],
    [breweryId, 'Brewery ID is required'],
    [name, 'Beer name is required'],
    [style, 'Beer style is required'],
  ];

  for (const [value, message] of requiredFieldChecks) {
    if (!isNonEmptyTrimmedString(value)) {
      return { error: message };
    }
  }

  if (!Number.isFinite(abv) || abv < 0) {
    return { error: 'ABV must be a non-negative number' };
  }

  if (!Number.isInteger(abv * 10)) {
    return { error: 'ABV must have maximum 1 decimal place' };
  }

  return {
    beerId,
    breweryId,
    name,
    style,
    abv,
    description,
    styleName,
    styleType,
  };
}

function buildUpdateBeerRequestBody({
  beerId,
  breweryId,
  name,
  style,
  abv,
  description,
  styleName,
  styleType,
}) {
  const requestBody = {
    beerId: beerId.trim(),
    breweryId: breweryId.trim(),
    name: name.trim(),
    style: style.trim(),
    abv,
    description: description?.trim() || null,
  };
  const styleNameTrim = trimIfString(styleName);
  const styleTypeTrim = trimIfString(styleType);
  if (styleNameTrim && styleTypeTrim) {
    requestBody.styleName = styleNameTrim;
    requestBody.styleType = styleTypeTrim;
  }
  return requestBody;
}

/**
 * useBeerUpdate
 * Provides a callable-based update operation for beers.
 *
 * @returns {{ updateBeer: Function, loading: boolean, errorMessage: string, clearError: Function }}
 */
export function useBeerUpdate() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  /**
   * Update a beer.
   *
   * @param {Object} beerData - Beer update data
   * @param {string} beerData.beerId - Document ID of the beer to update
   * @param {string} beerData.breweryId - Must exist in breweries collection
   * @param {string} beerData.name - Beer name (will be trimmed)
   * @param {string} beerData.style - Must exist as a document ID in beerStyles collection (will be trimmed)
   * @param {number} beerData.abv - Must be a non-negative number with maximum 1 decimal place
   * @param {string|null} beerData.description - Optional beer description
   * @returns {Promise<{ updated: boolean, beerId: string, status: string, error?: string }>}
   */
  const updateBeer = useCallback(async (beerData) => {
    const validation = validateUpdateBeerInput(beerData);
    if (validation.error) {
      return { updated: false, beerId: '', status: '', error: validation.error };
    }

    const {
      beerId,
      breweryId,
      name,
      style,
      abv,
      description,
      styleName,
      styleType,
    } = validation;

    if (!isMountedRef.current) {
      return { updated: false, beerId: '', status: '' };
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const updateBeerFn = httpsCallable(
        functions,
        'callableBeerUpdateBeer-updateBeer',
      );
      const requestBody = buildUpdateBeerRequestBody({
        beerId,
        breweryId,
        name,
        style,
        abv,
        description,
        styleName,
        styleType,
      });

      const result = await updateBeerFn(requestBody);

      const response = result?.data || {};

      return {
        updated: response.updated === true,
        beerId: response.beerId || beerId,
        status: response.status || '',
      };
    } catch (err) {
      Sentry.captureException(err);
      if (isMountedRef.current) {
        const friendly =
          err?.message || 'Failed to update beer. Please try again or contact support.';
        setErrorMessage(friendly);
        return {
          updated: false,
          beerId: beerId || '',
          status: '',
          error: friendly,
        };
      }
      return {
        updated: false,
        beerId: beerId || '',
        status: '',
      };
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  return {
    updateBeer,
    loading,
    errorMessage,
    clearError,
  };
}
